// Support centre + boards (VRG-226). API client for /support and /internal/support.
//
// Types are declared locally rather than imported from `@repo/types`, matching the
// house convention: apps/platform does NOT depend on that package (see
// `constants/shippingBoxPresets.ts` and `hooks/api/useCertificateTriggerSettings.ts`,
// which both say so explicitly). The canonical definitions live in
// `packages/types/src/support/support.interface.ts`; keep the two in step.

import { createApiCall } from './index'

export type SupportRequestType = 'support' | 'bug' | 'feature'

/**
 * Which SURFACE a request was filed from (VRG-378). A discriminator, not a
 * classification: `type` says what the person wants, `source` says what code they
 * were running when they asked.
 *
 * `certificates_app` is declared but never written yet — that surface is blocked
 * on VRG-377 and on the anonymous-abuse question.
 */
export type SupportRequestSource = 'platform' | 'scanner_app' | 'certificates_app'

/**
 * Which population of the internal queue to load (VRG-378).
 *
 * `brands` is the default because that queue is the tool staff use to SERVE
 * CUSTOMERS, and Vera's own reports answer a different question — mixed in they
 * inflate every count and put a staffer's note about a broken preview beside a
 * paying brand's outage.
 */
export type SupportQueueScope = 'brands' | 'internal' | 'all'

export type SupportRequestStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_on_customer'
  | 'resolved'
  | 'closed'

export type BoardItemStatus =
  | 'under_review'
  | 'planned'
  | 'in_progress'
  | 'shipped'
  | 'declined'
  | 'merged'

export interface SupportMessage {
  id: string
  requestId: string
  author: 'customer' | 'vera'
  authorName: string | null
  body: string
  /** Every attachment on this message, in attach order. Always an array, never null. */
  attachmentUrls: string[]
  /** Status changes and promotions. Rendered as a timeline note, not a reply. */
  isSystem: boolean
  createdAt: string
}

export interface SupportRequest {
  id: string
  reference: number
  companyId: string
  companyName: string | null
  userId: string | null
  submitterName: string | null
  submitterEmail: string | null
  type: SupportRequestType
  status: SupportRequestStatus
  body: string
  /** Every attachment, in attach order. Always an array, never null. */
  attachmentUrls: string[]
  /** Auto-captured server-side — shown as context, never as a form field. */
  pageUrl: string | null
  userAgent: string | null
  /**
   * VRG-378 build context — which surface, which deployment, which build.
   *
   * `source` is NOT NULL server-side, but a row written by a session running
   * pre-0083 code can still come back without it, so treat a missing value as
   * 'platform' rather than switching on it blind.
   */
  source: SupportRequestSource
  environment: string | null
  appVersion: string | null
  clientContext: Record<string, unknown> | null
  boardItemId: string | null
  /**
   * The board item a link from this request should point at — `boardItemId` unless that item
   * has since been merged, in which case it is the surviving target it was merged into.
   *
   * Link with THIS, not with `boardItemId`. Merged items are filtered out of the board, so a
   * link built from the raw column lands on a board that does not contain the item it names —
   * a dead end for exactly the submitter whose request was consolidated.
   */
  boardItemActiveId: string | null
  /**
   * The BOARD status governing this request, resolved through merges (VRG-324).
   *
   * Do not render this AND `status` on the same row. Company → Support shows one
   * pill per row and it must mean one thing: a promoted feature shows this, an
   * unpromoted row shows `status`. The two vocabularies share the value
   * `in_progress` while meaning different things by it, so mixing them is worse
   * than a missing pill. `displayStatusKey` in SupportListControls is the single
   * place that decides.
   */
  boardItemStatus: BoardItemStatus | null
  /** How many of the company's requests that board item tracks. 0 when unpromoted. */
  boardRequestCount: number
  /**
   * VRG-427 — filed by `ErrorBoundary` with no user action. Every surface that
   * lists requests must show this distinctly rather than letting an automatic
   * report read as something the submitter wrote.
   */
  isSystemGenerated: boolean
  /** How many times this exact (user, error) has recurred. 1 on every human-written request. */
  occurrenceCount: number
  lastSeenAt: string | null
  /** VRG-726 — when the user claimed this automatic report with a note (VRG-427). Null otherwise. */
  userNoteAt?: string | null
  /**
   * VRG-726 — on an automatic report, how many person-written requests link to it. 0 on a
   * person-written row. Optional only so a response from an API older than this field reads as 0.
   */
  linkedUserReportCount?: number
  messageCount: number
  createdAt: string
  updatedAt: string
}

export interface SupportRequestDetail extends SupportRequest {
  messages: SupportMessage[]
}

export interface BoardItem {
  id: string
  kind: 'integration' | 'feature'
  title: string
  description: string | null
  status: BoardItemStatus
  companyId: string | null
  /**
   * 0..5 relative popularity. There is deliberately NO voteCount on the
   * company-facing shape — the API does not send one. See PriorityIndicator.
   */
  priorityTier: number
  hasVoted: boolean
  submittedByName: string | null
  resolutionNote: string | null
  mergedIntoItemId: string | null
  /**
   * How many of your company's requests were promoted onto this item, including any that
   * arrived on an item since merged into it.
   *
   * This is the field that makes the board and "My requests" legible as ONE pipeline: a
   * board item is a PROMOTION of one or more requests, not a second copy of them. Without
   * it the two surfaces read as duplicate lists of the same idea, which is exactly the
   * confusion this was added to answer.
   *
   * Not the same disclosure as a vote count, which the API deliberately never sends to a
   * company caller — see `priorityTier`.
   */
  requestCount: number
  createdAt: string
}

/** Staff shape: adds the raw count, because staff act on it. */
export interface BoardItemInternal extends BoardItem {
  voteCount: number
  companyName: string | null
  sourceRequestId: string | null
  /**
   * True when this row IS a cross-company THEME — `companyId === null`.
   *
   * Sent by the API rather than derived here. A theme's title is distilled from
   * several brands' requests, so a client that got the null check backwards would
   * be rendering one where it does not belong; the polarity is decided once,
   * server-side, in `listBoardInternal`.
   */
  isTheme: boolean
  /** The theme this company item is grouped under, if any. Null on a theme. */
  themeItemId: string | null
  themeTitle: string | null
  /**
   * The ranking figure: how many DISTINCT companies asked for this theme.
   *
   * Raw votes over-weight a large org — one 200-seat brand outranks forty
   * companies, which is backwards for "what should we build". `voteCount` stays
   * as the secondary figure rather than being removed.
   */
  distinctCompanyCount: number
  linkedItemCount: number
  /**
   * Who asked. Staff-only by construction: this field exists only on the internal
   * shape, which is only ever returned from `/internal/support/*`. It is a list of
   * OTHER brands' names — it must never appear on `BoardItem`.
   */
  linkedCompanyNames: string[]
}

/**
 * A candidate the intake offers while you type: "someone already asked for this".
 *
 * No submitter name or email, deliberately — see the API's `SimilarRequestRow`.
 * The disclosure is "your company already asked for this", which is all the
 * decision in front of the user needs.
 *
 * `SimilarSupportRequest`, not `SimilarRequest`: `integrationRequestsApi` already
 * exports that name for the integrations board's own suggest-as-you-type flow, and
 * both are re-exported through `services/index.ts`, so the shorter name is a
 * barrel collision (TS2308). The clash is worth noticing rather than just renaming
 * past — dedupe-at-intake now exists TWICE in this codebase, on different data
 * (a public catalog matched by name vs. one company's requests matched by body).
 * Deferred as VRG-326-Support.Dedupe.Suggestions.Shared.Component rather than
 * extracted here; see the TODO on `SimilarSuggestions`.
 */
export interface SimilarSupportRequest {
  requestId: string
  reference: number
  body: string
  createdAt: string
  boardItemActiveId: string | null
  boardItemTitle: string | null
  boardItemStatus: BoardItemStatus | null
  /** How many of your company's requests already ask for this. Always ≥ 1. */
  requestCount: number
}

export interface SupportPermissions {
  canViewCompanyRequests: boolean
  canVote: boolean
}

/**
 * An error that carries the HTTP status that produced it.
 *
 * The status has to survive, because the UI's correct response differs by it: a
 * 403 means "you are not allowed to see this" and a 500 means "we could not
 * load it", and collapsing both into a bare `Error` is what let a 403 render as
 * a friendly "nothing here yet" empty state — telling an operator there were no
 * support requests when in fact there were, and they simply could not see them.
 */
export class SupportApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'SupportApiError'
  }
}


// Unwraps the response body. The status guard is belt-and-braces now that `createApiCall`
// rejects on every >= 400 (VRG-386) — a non-2xx no longer reaches here. It is NOT what
// `isForbidden` reads any more; see above.
const ok = (response: any, fallback: string) => {
  if (response.status !== 200 && response.status !== 201) {
    throw new SupportApiError(response?.data?.message || fallback, response?.status ?? 0)
  }
  return response.data
}

export const supportApi = {
  async list(scope: 'mine' | 'company' = 'mine'): Promise<SupportRequest[]> {
    const response = await createApiCall({ method: 'GET', url: `/support?scope=${scope}` })
    return ok(response, 'Failed to load your requests')
  },

  async permissions(): Promise<SupportPermissions> {
    const response = await createApiCall({ method: 'GET', url: '/support/permissions' })
    return ok(response, 'Failed to load support permissions')
  },

  async detail(id: string): Promise<SupportRequestDetail> {
    const response = await createApiCall({ method: 'GET', url: `/support/${id}` })
    return ok(response, 'Failed to load this request')
  },

  /**
   * Upload one attachment; returns the URL to store on the request.
   *
   * Deliberately NOT folded into `create`. Two calls is what lets a failed
   * upload cost the user their FILE rather than the paragraph they just typed —
   * see the form's `submit`, which sends the request either way.
   *
   * Do NOT set a Content-Type here: `createApiCall` detects FormData and omits
   * its default `application/json` precisely so the browser can generate the
   * multipart boundary (services/index.ts). Setting it by hand produces a
   * boundary-less header and the server reads an empty body.
   */
  async uploadAttachment(file: File): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await createApiCall({
      method: 'POST',
      url: '/support/attachments',
      data: formData,
    })
    return ok(response, "Couldn't upload that file")
  },

  /**
   * "Has someone already asked for this?" — YOUR company only.
   *
   * The scope is not a parameter: the API takes it off the authenticated caller,
   * so there is no argument here that could widen it. Ungated on purpose (a member
   * without `View company requests` still gets suggestions) — see the endpoint's
   * own note for why that is the design and not an oversight.
   */
  async searchSimilar(query: string): Promise<SimilarSupportRequest[]> {
    const response = await createApiCall({
      method: 'GET',
      url: `/support/similar?q=${encodeURIComponent(query)}`,
    })
    return ok(response, 'Failed to look for similar requests')
  },

  async create(payload: {
    type: SupportRequestType
    /**
     * VRG-266 — what the request is ABOUT, orthogonal to `type` (what you WANT). Set by
     * an intake that knows its own subject, such as the tag shop's custom-tag enquiry.
     * Omitted on ordinary requests, which is nearly all of them.
     */
    topic?: 'custom_tag' | null
    body: string
    /** One URL per attached file, in attach order, from the upload endpoint. */
    attachmentUrls?: string[] | null
    /** The route the user was on. Context, captured by the form — not typed in. */
    pageUrl?: string | null
    /** An existing request of YOUR company to add this one to (VRG-324). */
    linkToRequestId?: string | null
    /**
     * VRG-378 build context. Captured by the form, never typed in — the browser is
     * the only party that knows which bundle it was served, and asking a user for
     * it is the canonical example of a form field that should not exist.
     *
     * `source` is NOT sent: this client is the platform by definition, and letting
     * a caller name its own surface would make the discriminator a claim rather
     * than a fact. The server sets it.
     */
    environment?: string | null
    appVersion?: string | null
    clientContext?: Record<string, unknown> | null
  }): Promise<SupportRequestDetail> {
    const response = await createApiCall({ method: 'POST', url: '/support', data: payload })
    return ok(response, 'Failed to send your request')
  },

  /**
   * VRG-427 — the AUTOMATIC half of `create`. Called by `errorReporter.ts` from
   * inside `ErrorBoundary.componentDidCatch`, never from a form. No `type`
   * (always `bug` server-side), no attachments, no dedupe-at-intake link — none
   * of those apply to a report nobody typed.
   */
  async reportAutomaticError(payload: {
    errorMessage: string
    componentStack?: string | null
    /** VRG-721 — which automatic path. The API defaults to `render`. */
    kind?: 'render' | 'toast' | 'uncaught' | 'rejection'
    pageUrl?: string | null
    environment?: string | null
    appVersion?: string | null
    clientContext?: Record<string, unknown> | null
  }): Promise<{ id: string; reference: number; occurrenceCount: number; deduped: boolean }> {
    const response = await createApiCall({
      method: 'POST',
      url: '/support/error-reports',
      data: payload,
    })
    return ok(response, 'Failed to auto-report this error')
  },

  /**
   * VRG-427 (revised) — the user's optional "what were you doing?", attached to
   * the report the boundary already filed.
   *
   * This is what CLAIMS the report. Until it is called the report exists, Vera
   * has it, and the user's own support list does not carry a ticket they never
   * wrote — the report belongs to Vera by default and to the user by choice. So
   * this is never called with an empty note: an empty note is not a shorter
   * claim, it is the user declining to make one.
   *
   * Called from the crash modal, which runs on the page the user was REDIRECTED
   * to — a normal, healthy page. Unlike `reportAutomaticError` this is not inside
   * the error path, so a failure here is an ordinary failed request and may be
   * surfaced to the user.
   */
  async addErrorReportNote(id: string, note: string): Promise<{ id: string; reference: number }> {
    const response = await createApiCall({
      method: 'POST',
      url: `/support/error-reports/${id}/note`,
      data: { note },
    })
    return ok(response, 'Failed to add your note')
  },

  async reply(id: string, payload: { body: string; attachmentUrls?: string[] | null }): Promise<SupportMessage[]> {
    const response = await createApiCall({
      method: 'POST',
      url: `/support/${id}/messages`,
      data: payload,
    })
    return ok(response, 'Failed to post your reply')
  },

  /*
    `board()` and `toggleVote()` were here. Removed with the customer feature board
    (VRG-324): the board page is gone, so both had no caller, and a shipped
    `toggleVote` with nothing calling it is a vote affordance one import away from
    coming back. AC6 — "a single-seat company sees no vote affordance anywhere" —
    is now true by ABSENCE rather than by a conditional.

    The endpoints themselves stay (see the note in hooks/api/useSupport.ts).
  */

  // ── Internal (Vera staff) ────────────────────────────────────────────────
  async internalList(
    status?: SupportRequestStatus,
    scope?: SupportQueueScope,
  ): Promise<SupportRequest[]> {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    // Sent explicitly even for the default. The server defaults to `brands` too,
    // but a URL that states the scope is one a staffer can read in a network log
    // and understand without knowing what the default is.
    if (scope) params.set('scope', scope)
    const qs = params.toString()
    const response = await createApiCall({
      method: 'GET',
      url: `/internal/support${qs ? `?${qs}` : ''}`,
    })
    return ok(response, 'Failed to load the support queue')
  },

  /**
   * One company's FULL support queue, for the internal company-detail Support tab
   * (VRG-515/516).
   *
   * Deliberately not `internalList` with a client-side company filter, and not
   * `list('company')`:
   *
   *  - `list('company')` is the CUSTOMER-facing read, which hides automatically-filed
   *    crash reports the user never claimed. Correct for a brand looking at its own
   *    queue; wrong here, since the unclaimed crash rows are exactly what staff are
   *    looking for when they ask how much trouble a company is in.
   *  - `internalList` scopes by `companies.kind` across all companies, so narrowing it
   *    to one company would mean fetching the whole queue to discard nearly all of it.
   *
   * `summary` carries CURRENT status counts. `support_requests` has no status-history
   * table — a request moving `open → resolved` overwrites the column — so these are a
   * snapshot of now. Trends over `created_at` are sound (it is immutable); anything
   * keyed on status is not.
   *
   * Rows come back newest-first.
   */
  async companyRequests(
    companyId: string,
    status?: SupportRequestStatus,
  ): Promise<{ items: SupportRequest[]; summary: { total: number; byStatus: Record<string, number> } }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : ''
    const response = await createApiCall({
      method: 'GET',
      url: `/companies/admin/${companyId}/support-requests${qs}`,
    })
    return ok(response, 'Failed to load this company’s support requests')
  },

  /**
   * VRG-721 — open brand requests, for the internal sidebar's Support badge. A
   * count endpoint rather than `internalList().length`: this is polled on every
   * staff page, and the list carries every row's body.
   */
  async internalOpenCount(): Promise<number> {
    const response = await createApiCall({ method: 'GET', url: '/internal/support/open-count' })
    const data: { open?: number } = ok(response, 'Failed to load the open count')
    return typeof data?.open === 'number' ? data.open : 0
  },

  async internalDetail(id: string): Promise<SupportRequestDetail> {
    const response = await createApiCall({ method: 'GET', url: `/internal/support/${id}` })
    return ok(response, 'Failed to load this request')
  },

  /**
   * Reply as Vera. Takes `attachmentUrls` for the same reason the customer reply does:
   * "here is the export you asked about" is as much a staff answer as a customer one,
   * and `POST /internal/support/:id/messages` has always accepted the field
   * (`support-internal.controller.ts`) — only this client shape was narrower than the
   * endpoint, which is what made the staff composer look impossible to give a file
   * picker. Same wire shape as `reply` above, deliberately: one thread, one message
   * schema, whichever side wrote it.
   */
  async internalReply(
    id: string,
    payload: { body: string; attachmentUrls?: string[] | null },
  ): Promise<SupportMessage[]> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/support/${id}/messages`,
      data: payload,
    })
    return ok(response, 'Failed to post your reply')
  },

  async internalSetStatus(id: string, status: SupportRequestStatus): Promise<SupportRequestDetail> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/support/${id}/status`,
      data: { status },
    })
    return ok(response, 'Failed to change the status')
  },

  async internalPromote(id: string, title?: string): Promise<BoardItemInternal> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/support/${id}/promote`,
      data: { title },
    })
    return ok(response, 'Failed to promote this request')
  },

  async internalBoard(kind?: 'feature' | 'integration'): Promise<BoardItemInternal[]> {
    const qs = kind ? `?kind=${kind}` : ''
    const response = await createApiCall({ method: 'GET', url: `/internal/support/board${qs}` })
    return ok(response, 'Failed to load the board queue')
  },

  async internalSetBoardStatus(
    id: string,
    payload: { status: BoardItemStatus; note?: string },
  ): Promise<BoardItemInternal> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/support/board/${id}/status`,
      data: payload,
    })
    return ok(response, 'Failed to change the status')
  },

  /**
   * Create a cross-company theme. `fromItemId` links an existing item to it in the
   * same call — the common case, since a theme is almost always noticed while
   * looking at the second company that asked for the same thing.
   */
  async internalCreateTheme(payload: {
    title: string
    description?: string
    fromItemId?: string
  }): Promise<BoardItemInternal> {
    const response = await createApiCall({
      method: 'POST',
      url: '/internal/support/board/themes',
      data: payload,
    })
    return ok(response, "Couldn't create that theme")
  },

  /** Link an item to a theme, or unlink it by passing null. */
  async internalSetItemTheme(
    id: string,
    themeItemId: string | null,
  ): Promise<BoardItemInternal> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/support/board/${id}/theme`,
      data: { themeItemId },
    })
    return ok(response, "Couldn't change this item's theme")
  },

  async internalMergeBoardItems(sourceId: string, targetItemId: string): Promise<BoardItemInternal> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/support/board/${sourceId}/merge`,
      data: { targetItemId },
    })
    return ok(response, 'Failed to merge')
  },
}
